
import React from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { getBlogsBySlug } from "../helpers/api/BlogService";
import { toast } from 'react-toastify';
import { Navbar1 } from "components/navbars";
import { Footer1 } from "components/footer";
import Seo from "components/Seo";
import FeatherIcon from 'feather-icons-react';
import '../styles/industry-cards.css';
import BackToTop from "components/BackToTop";
import { usePageTracking } from "helpers/usePageTracking";


import ScaleIcon from "../assets/images/icons/duotone-icons/general/Scale.svg";
import CalculatorIcon from "../assets/images/icons/duotone-icons/shopping/Calculator.svg";
import DollarIcon from "../assets/images/icons/duotone-icons/shopping/Dollar.svg";
import WalletIcon from "../assets/images/icons/duotone-icons/shopping/Wallet.svg";
import ShieldCheckIcon from "../assets/images/icons/duotone-icons/general/Shield-check.svg";
import Price1Icon from "../assets/images/icons/duotone-icons/shopping/Price #1.svg";
import UserIcon from "../assets/images/icons/duotone-icons/general/User.svg";
import GroupIcon from "../assets/images/icons/duotone-icons/communication/Group.svg";
import AddUserIcon from "../assets/images/icons/duotone-icons/communication/Add-user.svg";
import FileDoneIcon from "../assets/images/icons/duotone-icons/files/File-done.svg";
import { colors } from "styles/colors";

const industries = [
  {
    key: "law-firms",
    title: "CRM for Law Firms",
    desc: [
      "Capture every lead & inquiry",
      "Automate follow-ups and reminders",
      "Track cases and deadlines easily"
    ],
    iconType: "svg",
    icon: ScaleIcon
  },
  {
    key: "accountants",
    title: "CRM for Accountants",
    desc: [
      "Never miss deadlines or follow-ups",
      "Automate reminders & workflows",
      "Track every client and opportunity"
    ],
    iconType: "svg",
    icon: CalculatorIcon
  },
  {
    key: "financial-advisors",
    title: "CRM for Financial Advisors",
    desc: [
      "Track every client & opportunity",
      "Automate follow-ups and reminders",
      "Segment clients for better service"
    ],
    iconType: "feather",
    icon: "dollar-sign"
  },
  {
    key: "mortgage-brokers",
    title: "CRM for Mortgage Brokers",
    desc: [
      "Capture and track every lead & deal",
      "Automate follow-ups and document reminders",
      "See pipeline status and close more loans"
    ],
    iconType: "feather",
    icon: "credit-card"
  },
  {
    key: "insurance-agents",
    title: "CRM for Insurance Agents",
    desc: [
      "Track every lead, policy & renewal",
      "Automate follow-ups and reminders",
      "Boost conversions and client retention"
    ],
    iconType: "feather",
    icon: "shield"
  },
  {
    key: "tax-consultants",
    title: "CRM for Tax Consultants",
    desc: [
      "Track clients and filing workflows",
      "Automate document and deadline reminders",
      "Boost revenue with repeat business"
    ],
    iconType: "feather",
    icon: "file-text"
  },
  {
    key: "business-consultants",
    title: "CRM for Business Consultants",
    desc: [
      "Track leads, deals, and clients easily",
      "Automate follow-ups and proposals",
      "Boost conversions and scale your business"
    ],
    iconType: "feather",
    icon: "users"
  },
  {
    key: "hr-consultants",
    title: "CRM for HR Consultants",
    desc: [
      "Track clients, jobs, and candidate pipelines",
      "Automate follow-ups and interview reminders",
      "Improve placements and client satisfaction"
    ],
    iconType: "feather",
    icon: "users"
  },
  {
    key: "recruitment-agencies",
    title: "CRM for Recruitment Agencies",
    desc: [
      "Track clients, jobs, and candidate pipelines",
      "Automate follow-ups and interview reminders",
      "Place candidates faster and scale your agency"
    ],
    iconType: "feather",
    icon: "user-plus"
  },
  {
    key: "auditors",
    title: "CRM for Auditors",
     desc: [
      "Track clients and audit engagements",
      "Automate reminders and workflow tasks",
      "Ensure deadlines and boost efficiency"
    ],
    iconType: "feather",
    icon: "check-circle"
  },
];

export default function Industries() {
  const seo = usePageTracking("industries");
  const navigate = useNavigate();

  // Helper to convert title to slug
  const titleToSlug = (title: string) =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "")
      .replace(/ /g, "-");

  // Card click handler
  const handleCardClick = async (title: string) => {
    const industryKey = titleToSlug(title);
    try {
      const data = await getBlogsBySlug(industryKey);
      if (data && data.success && data.result) {
        navigate(`/${industryKey}`, {
          state: {
            type: "industry",   // ✅ IMPORTANT
            data: data.result
          }
        });
      } else {
        toast.info("No details found for this industry.");
      }
    } catch (err) {
      toast.error("Failed to fetch industry details.");
    }
  };

  return (
    <>
      {seo && (
        <Seo
          pageName="industries"
          title="CRM for Every Industry | SalesTrackify CRM Solutions"
          description="Discover industry-specific CRM solutions by SalesTrackify. Built for law firms, accountants, financial advisors, recruiters, and more."
          url="https://salestrackify.com/industries"
          image="/seo/trackify-preview.png"
          keywords={industries.map(ind => ind.title).join(', ')}
        />
      )}
      <Navbar1
        hideSearch
        fixedWidth
        navClass="navbar-light zindex-10"
        buttonClass="btn-outline-secondary btn-sm"
      />
      <div className="">
        <section
          style={{
            background: 'linear-gradient(90deg, #1e3a8a22, #2dd4bf22, #1e3a8a22)',
            padding: '80px 0',
            textAlign: 'center',
            borderBottomLeftRadius: '30px',
            borderBottomRightRadius: '30px',
            marginLeft: 25,
            marginRight: 25,
            marginBottom: '2.5rem',
          }}
        >

          <div
            className="d-inline-flex align-items-center mb-4"
            style={{
              gap: '8px',
              background: 'rgba(36,52,125,0.08)',
              borderRadius: '50px',
              padding: '8px 20px',
              border: '1px solid rgba(36,52,125,0.1)',
            }}
          >
            <FeatherIcon icon="zap" style={{ width: '14px', height: '14px', color: colors.primary.darkBlue }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: colors.primary.darkBlue }}>
             Industries
            </span>
          </div>
          <h1
            style={{
              fontSize: '48px',
              fontWeight: 700,
              color: '#24347D',
            }}
          >
            CRM for Every Industry | SalesTrackify CRM Solutions
          </h1>
          <p style={{ marginTop: '10px', fontSize: '18px', color: '#6c757d', textAlign: 'center', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }} className="px-5">
            Discover industry-specific CRM solutions by SalesTrackify. Built for law firms, accountants, financial advisors, recruiters, and more.
          </p>
        </section>
        <section className="container industry-sub-header text-center">
          <h2>Industry-Specific CRM Built for Your Business</h2>
          <p>
            SalesTrackify is designed to adapt to your industry workflows — from managing clients and pipelines to automating support and reporting.
          </p>
          {/* <Link to="/signup" className="btn btn-primary mx-2">Get Started Free</Link> */}
          {/* <a href="/contact" className="btn btn-outline-primary mx-2">Book a Demo</a> */}
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition-all hover:scale-105 shadow-lg"
            style={{
              backgroundColor: "#26A2AB",
              color: "white"
            }}
          >
            Book a Demo
          </Link>
        </section>
        <section className="container my-5">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div style={{
                background: 'linear-gradient(90deg, #f8fafc 80%, #e0f2fe 100%)',
                borderRadius: 24,
                boxShadow: '0 2px 16px 0 rgba(36,52,125,0.08)',
                padding: '36px 32px',
                marginBottom: 24,
                border: '1.5px solid #e5e7eb',
                position: 'relative',
              }}>
                <div className="d-flex align-items-start mb-3">
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #2cd8d5 60%, #6b8dd6 100%)',
                    borderRadius: '50%',
                    width: 54,
                    height: 54,
                    marginRight: 18,
                  }}>
                    <FeatherIcon icon="layers" size={28} color="#fff" />
                  </span>
                  <div>
                    <h3 style={{ fontWeight: 700, color: '#24347D', fontSize: 22, marginBottom: 8 }}>Industry-Specific CRM Challenges</h3>
                    <p style={{ fontSize: 17, color: '#374151', marginBottom: 0 }}>
                      Every industry has unique processes, compliance requirements, and customer expectations. A generic CRM often fails to address these nuances.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-start">
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #6b8dd6 60%, #2cd8d5 100%)',
                    borderRadius: '50%',
                    width: 54,
                    height: 54,
                    marginRight: 18,
                  }}>
                    <FeatherIcon icon="cpu" size={28} color="#fff" />
                  </span>
                  <div>
                    <h3 style={{ fontWeight: 700, color: '#24347D', fontSize: 22, marginBottom: 8 }}>How SalesTrackify Helps</h3>
                    <p style={{ fontSize: 17, color: '#374151', marginBottom: 0 }}>
                      SalesTrackify provides customizable, AI-powered CRM solutions tailored for different industries — helping you manage leads, clients, support tickets, and revenue pipelines efficiently.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="container industry-sub-header">
          <h2 className="mb-4 text-center">Industries We Serve</h2>
          <div className="row g-4 justify-content-center">
            {industries.map((ind, idx) => (
              <div className="col-md-6 col-lg-5 d-flex justify-content-center mb-4" key={ind.key}>
                <div
                  role="button"
                  tabIndex={0}
                  className="industry-card-horizontal"
                  onClick={() => handleCardClick(ind.title)}
                  onKeyPress={e => { if (e.key === 'Enter') handleCardClick(ind.title); }}
                >
                  <div className="industry-card-accent" />
                  <div className="industry-card-icon-wrap">
                    <div className="industry-card-icon-inner">
                      {ind.iconType === 'svg' ? (
                        <img src={ind.icon} alt={ind.title + ' icon'} width={32} height={32} />
                      ) : (
                        <FeatherIcon icon={ind.icon} size={32} color="#fff" />
                      )}
                    </div>
                  </div>
                  <div className="industry-card-content">
                    <h3 className="fw-bold mb-2 text-dark industry-card-title">{ind.title}</h3>
                    <ul className="industry-card-list">
                      {ind.desc.map((point: string, i: number) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* <section className="container mt-5 text-center mb-5">
          <h2>Start managing your business smarter with SalesTrackify CRM.</h2>

          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition-all hover:scale-105 shadow-lg"
            style={{
              backgroundColor: "#26A2AB",
              color: "white"
            }}
          >
            Book a Demo
          </Link>
        </section> */}
      </div>
      <Footer1 />
      <BackToTop />
    </>
  );
}
